import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile } from '../../utils/api';
import { 
  FileText, Download, Trash2, Plus, Upload, Calendar, Edit, Save, 
  Search, Filter, ChevronRight, Clock, User, Loader2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import TwoStepUpload from '../../components/TwoStepUpload';
import useAuthStore from '../../store/authStore';

// Color palette for years
const YEAR_COLORS = {
  2026: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500' },
  2025: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-500' },
  2024: { gradient: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-500' },
  2023: { gradient: 'from-orange-500 to-amber-600', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-500' },
  2022: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-500' },
  default: { gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-500' }
};

const getYearColor = (dateStr) => {
  const year = new Date(dateStr).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS.default;
};

const Minutes = () => {
  const { user } = useAuthStore();
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMinute, setEditingMinute] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Create form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [date, setDate] = useState('');
  const [stagedFileUrl, setStagedFileUrl] = useState(null);
  const [stagedFileName, setStagedFileName] = useState(null);
  
  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editFile, setEditFile] = useState(null);

  const { getRootProps: getEditRootProps, getInputProps: getEditInputProps, isDragActive: isEditDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => setEditFile(acceptedFiles[0]),
  });

  useEffect(() => { loadMinutes(); }, []);

  const loadMinutes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/minutes');
      setMinutes(response.data);
    } catch (error) {
      toast.error('Failed to load minutes');
    } finally {
      setLoading(false);
    }
  };

  // Get unique years for filter
  const years = useMemo(() => {
    const uniqueYears = [...new Set(minutes.map(m => new Date(m.date).getFullYear()))];
    return uniqueYears.sort((a, b) => b - a);
  }, [minutes]);

  // Filtered minutes
  const filteredMinutes = useMemo(() => {
    return minutes.filter(m => {
      const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (m.content && m.content.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesYear = selectedYear === 'all' || new Date(m.date).getFullYear().toString() === selectedYear;
      return matchesSearch && matchesYear;
    });
  }, [minutes, searchQuery, selectedYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/minutes', {
        title, content, date: new Date(date).toISOString(), file_url: stagedFileUrl, uploaded_by: user.user_id,
      });
      toast.success('Minutes added successfully');
      setDialogOpen(false);
      resetForm();
      loadMinutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add minutes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSaved = (fileUrl, fileName) => {
    setStagedFileUrl(fileUrl);
    setStagedFileName(fileName);
  };

  const openEditDialog = (minute) => {
    setEditingMinute(minute);
    setEditTitle(minute.title);
    setEditContent(minute.content || '');
    setEditDate(minute.date ? minute.date.split('T')[0] : '');
    setEditFile(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingMinute) return;
    setLoading(true);
    try {
      let file_url = editingMinute.file_url;
      if (editFile) {
        const formData = new FormData();
        formData.append('file', editFile);
        const uploadResponse = await api.post('/files/upload', formData);
        file_url = uploadResponse.data.file_url;
      }
      await api.put(`/minutes/${editingMinute.id}`, {
        title: editTitle, content: editContent, date: new Date(editDate).toISOString(), file_url,
      });
      toast.success('Minutes updated successfully');
      setEditDialogOpen(false);
      setEditingMinute(null);
      loadMinutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update minutes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/minutes/${id}`);
      toast.success('Minutes deleted');
      loadMinutes();
    } catch (error) {
      toast.error('Failed to delete minutes');
    }
  };

  const resetForm = () => { setTitle(''); setContent(''); setDate(''); setStagedFileUrl(null); setStagedFileName(null); };

  const handleDownload = async (fileUrl, title) => {
    try {
      await downloadFile(fileUrl, title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-minutes-page">
      <BackButton to="/admin" />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-white">Minutes Management</h1>
            <p className="text-slate-400">Upload and manage meeting minutes</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg" data-testid="add-minutes-btn">
              <Plus className="w-4 h-4 mr-2" />Add Minutes
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
            <DialogHeader><DialogTitle className="text-white">Upload New Minutes</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Board Meeting - January 2024" required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Meeting Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Summary (Optional)</Label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Brief summary of the meeting..." rows={4} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Upload Document (Two-Step: Upload → Save)</Label>
                <TwoStepUpload 
                  onSaveComplete={handleFileSaved}
                  onCancel={() => { setStagedFileUrl(null); setStagedFileName(null); }}
                  label="Upload Minutes Document"
                  acceptedTypes={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  }}
                />
                {stagedFileUrl && (
                  <p className="text-sm text-emerald-400 mt-2">✓ File saved: {stagedFileName}</p>
                )}
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600">{loading ? 'Adding...' : 'Add Minutes'}</Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search minutes..."
            className="pl-10 bg-slate-700 border-slate-600 text-white"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Years</option>
            {years.map(year => (
              <option key={year} value={year.toString()}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-400 text-sm font-medium">Total Minutes</p>
          <p className="text-2xl font-bold text-white">{minutes.length}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-emerald-400 text-sm font-medium">This Year</p>
          <p className="text-2xl font-bold text-white">{minutes.filter(m => new Date(m.date).getFullYear() === new Date().getFullYear()).length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-xl p-4">
          <p className="text-purple-400 text-sm font-medium">With Documents</p>
          <p className="text-2xl font-bold text-white">{minutes.filter(m => m.file_url).length}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-4">
          <p className="text-orange-400 text-sm font-medium">Filtered</p>
          <p className="text-2xl font-bold text-white">{filteredMinutes.length}</p>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Edit Minutes</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Date</Label>
              <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Summary</Label>
              <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={4} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Replace Document (Optional)</Label>
              <div {...getEditRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${isEditDragActive ? 'border-blue-500 bg-blue-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
                <input {...getEditInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                {editFile ? <p className="text-sm text-blue-400">New: {editFile.name}</p> : <p className="text-sm text-slate-400">Drop to replace</p>}
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600"><Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Minutes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : filteredMinutes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredMinutes.map((minute) => {
            const colors = getYearColor(minute.date);
            const isExpanded = expandedId === minute.id;
            return (
              <div 
                key={minute.id} 
                className={`group relative bg-slate-800 rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10`}
                data-testid={`minute-card-${minute.id}`}
              >
                {/* Color stripe */}
                <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`bg-gradient-to-br ${colors.gradient} p-3 rounded-xl shadow-lg`}>
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEditDialog(minute)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg transition-colors" title="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      {minute.file_url && (
                        <button onClick={() => handleDownload(minute.file_url, minute.title)} className="p-2 text-slate-400 hover:text-green-400 hover:bg-slate-700 rounded-lg transition-colors" title="Download">
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(minute.id, minute.title)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Year Badge */}
                  <span className={`inline-block ${colors.badge} text-white text-xs px-2 py-1 rounded-full font-medium mb-3`}>
                    {new Date(minute.date).getFullYear()}
                  </span>
                  
                  {/* Title */}
                  <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                    {minute.title}
                  </h3>
                  
                  {/* Summary */}
                  {minute.content && (
                    <p className={`text-sm text-slate-400 mb-4 ${isExpanded ? '' : 'line-clamp-2'}`}>
                      {minute.content}
                    </p>
                  )}
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                    <div className="flex items-center text-xs text-slate-500 space-x-3">
                      <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{formatDate(minute.date)}</span>
                    </div>
                    {minute.content && minute.content.length > 100 && (
                      <button 
                        onClick={() => setExpandedId(isExpanded ? null : minute.id)}
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                        <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                    )}
                  </div>
                  
                  {/* Document indicator */}
                  {minute.file_url && (
                    <div className="absolute bottom-4 right-4">
                      <div className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full flex items-center">
                        <FileText className="w-3 h-3 mr-1" />PDF
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">No minutes found</p>
          <p className="text-sm text-slate-500 mt-2">
            {searchQuery || selectedYear !== 'all' ? 'Try adjusting your filters' : 'Click "Add Minutes" to upload your first document'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Minutes;
